import React, { useState, useEffect, useCallback } from 'react';
import { cldThumb } from '../utils/img';
import { getItemBrands } from '../api/inventory';
import { useSizes, useCategories, getUseCases } from '../api/meta';
import { getReturnableItems, confirmBrandReturn, getReturnsLog, getReturnReport } from '../api/brandReturns';

const PAGE_SIZE = 20;

// "2026-09-18" -> "18/09/2026"
function ddmmyyyy(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}
const money = n => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const csvCell = v => {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

// Matches the ZOCS Returns Report template: title, export date, blank, table.
function downloadReportCsv(reference, rows) {
  const lines = [
    ['ZOCS Returns Report'],
    ['Date of Export', ddmmyyyy(new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }))],
    [],
    ['Brand', 'Shipment Date', 'Return Value', 'Return Date', 'Reference'],
    ...rows.map(r => [r.brand, ddmmyyyy(r.shipment_date) || 'Unknown', money(r.return_value),
                      ddmmyyyy(r.return_date), r.reference]),
  ];
  const csv = lines.map(l => l.map(csvCell).join(',')).join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `${reference}.csv`; a.click();
  URL.revokeObjectURL(url);
}

const selectCls = 'border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 bg-white focus:outline-none focus:border-zoddle-green';

export default function BrandReturns() {
  const sizes = useSizes();
  const categories = useCategories();
  const [useCases, setUseCases] = useState([]);
  const [brands, setBrands] = useState([]);
  const [brand, setBrand] = useState('');
  const [filters, setFilters] = useState({ size: '', category: '', use_case: '', inbound_date: '' });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [inboundDates, setInboundDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(new Map());   // item_id -> item (survives filter changes)
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);
  const [log, setLog] = useState([]);

  const refreshLog = useCallback(() => { getReturnsLog().then(setLog).catch(() => {}); }, []);

  useEffect(() => {
    getItemBrands().then(d => setBrands(d.brands || [])).catch(() => {});
    getUseCases().then(setUseCases).catch(() => {});
    refreshLog();
  }, [refreshLog]);

  const fetchPage = useCallback(async (offset) => {
    if (!brand) return;
    setLoading(true); setError(null);
    try {
      const data = await getReturnableItems({ brand_name: brand, ...filters, offset, limit: PAGE_SIZE });
      setItems(prev => offset === 0 ? data.items : [...prev, ...data.items]);
      setTotal(data.total);
      setInboundDates(data.inbound_dates || []);
    } catch { setError('Failed to load items.'); }
    finally { setLoading(false); }
  }, [brand, filters]);

  useEffect(() => { setItems([]); setTotal(0); fetchPage(0); }, [fetchPage]);

  function changeBrand(b) {
    setBrand(b);
    setFilters({ size: '', category: '', use_case: '', inbound_date: '' });
    setSelected(new Map());
    setDone(null);
  }

  function toggle(item) {
    setSelected(prev => {
      const next = new Map(prev);
      next.has(item.item_id) ? next.delete(item.item_id) : next.set(item.item_id, item);
      return next;
    });
  }

  function selectAllLoaded() {
    setSelected(prev => {
      const next = new Map(prev);
      items.forEach(i => next.set(i.item_id, i));
      return next;
    });
  }

  const selectedValue = [...selected.values()].reduce((s, i) => s + (i.cost_with_tax || 0), 0);

  async function doConfirm() {
    setSaving(true); setError(null);
    try {
      const res = await confirmBrandReturn(brand, [...selected.keys()]);
      downloadReportCsv(res.reference, res.rows);
      setDone(res);
      setSelected(new Map());
      setConfirming(false);
      fetchPage(0);
      refreshLog();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to confirm return.');
      setConfirming(false);
    } finally { setSaving(false); }
  }

  async function redownload(r) {
    try {
      const rep = await getReturnReport(r.brand_return_id);
      downloadReportCsv(rep.reference, rep.rows);
    } catch { setError('Failed to download report.'); }
  }

  return (
    <div className="p-5 pb-24">
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <select value={brand} onChange={e => changeBrand(e.target.value)} className={selectCls}>
          <option value="">Select brand…</option>
          {brands.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        {brand && (
          <>
            <select value={filters.size} onChange={e => setFilters(f => ({ ...f, size: e.target.value }))} className={selectCls}>
              <option value="">All sizes</option>
              {sizes.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={filters.category} onChange={e => setFilters(f => ({ ...f, category: e.target.value }))} className={selectCls}>
              <option value="">All categories</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={filters.use_case} onChange={e => setFilters(f => ({ ...f, use_case: e.target.value }))} className={selectCls}>
              <option value="">All use-cases</option>
              {useCases.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
            <select value={filters.inbound_date} onChange={e => setFilters(f => ({ ...f, inbound_date: e.target.value }))} className={selectCls}>
              <option value="">All inbound dates</option>
              {inboundDates.map(d => <option key={d} value={d}>{ddmmyyyy(d)}</option>)}
            </select>
            <p className="text-xs text-gray-400 ml-1">
              {loading && items.length === 0 ? 'Loading…' : `Showing ${items.length} of ${total.toLocaleString()}`}
            </p>
            <div className="flex-1" />
            {items.length > 0 && (
              <button onClick={selectAllLoaded}
                className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 text-gray-600 hover:border-zoddle-blue font-medium">
                Select all shown ({items.length})
              </button>
            )}
          </>
        )}
      </div>

      {done && (
        <div className="mb-4 bg-green-50 border border-green-100 rounded-lg px-4 py-3 text-sm text-green-700 flex items-center gap-3">
          <span>Return <b>{done.reference}</b> saved — {done.units} pieces, ₹{money(done.total_value)}. Report downloaded.</span>
          <button onClick={() => downloadReportCsv(done.reference, done.rows)} className="underline font-semibold">Download again</button>
        </div>
      )}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-100 rounded-lg px-4 py-3 text-sm text-zoddle-pink">{error}</div>
      )}

      {!brand && <p className="text-gray-400 text-sm">Select a brand to see its pieces currently with Zoddle.</p>}
      {brand && !loading && items.length === 0 && !error && (
        <p className="text-gray-400 text-sm">No returnable pieces for this filter.</p>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
        {items.map(i => {
          const isSel = selected.has(i.item_id);
          return (
            <div key={i.item_id} onClick={() => toggle(i)}
              className={`border rounded-xl p-2 cursor-pointer transition ${
                isSel ? 'border-zoddle-green ring-2 ring-zoddle-green bg-zoddle-green/5' : 'border-gray-100 hover:border-gray-300'
              }`}>
              {i.image_url_1
                ? <img src={cldThumb(i.image_url_1, 300)} alt={i.design_name || ''} loading="lazy"
                    className="w-full aspect-square object-cover rounded-lg bg-gray-100 mb-2" />
                : <div className="w-full aspect-square rounded-lg bg-gray-100 mb-2 flex items-center justify-center text-gray-300 text-xs">No image</div>}
              <p className="text-sm font-mono font-bold text-zoddle-blue truncate">{i.zoddle_barcode}</p>
              <p className="text-xs text-gray-500 truncate">{i.size} · {i.gender} · {i.category}</p>
              <p className="text-xs text-gray-400 truncate">In {ddmmyyyy(i.inbound_date) || '—'} · ₹{money(i.cost_with_tax)}</p>
              {i.status !== 'in_bin' && (
                <span className={`inline-block mt-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                  i.status === 'damaged' ? 'bg-red-100 text-zoddle-pink' : 'bg-gray-100 text-gray-600'
                }`}>{i.status.replace(/_/g, ' ')}</span>
              )}
            </div>
          );
        })}
      </div>

      {items.length < total && (
        <div className="flex justify-center mt-6">
          <button onClick={() => fetchPage(items.length)} disabled={loading}
            className="text-xs border border-gray-200 rounded-lg px-4 py-1.5 text-gray-600 hover:border-zoddle-green font-medium disabled:opacity-40">
            {loading ? 'Loading…' : `Load ${Math.min(PAGE_SIZE, total - items.length)} more`}
          </button>
        </div>
      )}

      {log.length > 0 && (
        <div className="mt-10">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Past returns</h3>
          <table className="w-full text-sm bg-white border border-gray-100 rounded-lg">
            <thead>
              <tr className="text-left text-xs text-gray-400 border-b">
                <th className="px-3 py-2">Reference</th><th className="px-3 py-2">Brand</th>
                <th className="px-3 py-2">Pieces</th><th className="px-3 py-2">Value (incl GST)</th>
                <th className="px-3 py-2">Returned</th><th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {log.map(r => (
                <tr key={r.brand_return_id} className="border-b last:border-0">
                  <td className="px-3 py-2 font-mono">{r.reference}</td>
                  <td className="px-3 py-2">{r.brand}</td>
                  <td className="px-3 py-2">{r.units}</td>
                  <td className="px-3 py-2">₹{money(r.total_value)}</td>
                  <td className="px-3 py-2 text-gray-500">{r.returned_at}</td>
                  <td className="px-3 py-2">
                    <button onClick={() => redownload(r)} className="text-xs text-zoddle-blue font-semibold hover:underline">Download report</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-3 flex items-center gap-4 shadow-lg z-40">
          <span className="text-sm text-gray-700"><b>{selected.size}</b> selected · ₹{money(selectedValue)} cost incl GST</span>
          <button onClick={() => setSelected(new Map())} className="text-xs text-gray-500 underline">Clear</button>
          <div className="flex-1" />
          <button onClick={() => setConfirming(true)}
            className="bg-zoddle-pink text-white rounded-lg px-4 py-2 text-sm font-semibold">
            Confirm return to brand
          </button>
        </div>
      )}

      {confirming && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-2">Return to {brand}?</h3>
            <p className="text-sm text-gray-600 mb-1"><b>{selected.size}</b> pieces · ₹{money(selectedValue)} (cost incl GST)</p>
            <p className="text-sm text-gray-500 mb-5">All of them will be marked <b>returned to brand</b> and removed from inventory. This can't be undone from the app.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirming(false)} disabled={saving}
                className="text-sm border border-gray-200 rounded-lg px-4 py-2 text-gray-600">Cancel</button>
              <button onClick={doConfirm} disabled={saving}
                className="text-sm bg-zoddle-pink text-white rounded-lg px-4 py-2 font-semibold disabled:opacity-50">
                {saving ? 'Returning…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
